import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "src/common/utils";
import { ArtifactCard } from "src/components/ArtifactCard";
import { SourceLink } from "src/components/ArtifactCard/ArtifactCardContent";
import { Tooltip } from "src/components/ui/tooltip";

import { Source } from "./SourceReference";

/**
 * Maps query class names to user-friendly display names.
 * Converts "FooBarQuery" to "QueryFooBar" format for consistency with tool naming.
 */
function getQueryDisplayName(queryType: string | null | undefined): string {
  if (!queryType) return "Source";

  const queryTypeMap: Record<string, string> = {
    // Buildkite
    "BuildkiteLogQuery": "QueryBuildkiteLogs",
    "BuildkiteBuildQuery": "QueryBuildkiteBuilds",
    "BuildkiteAnnotationQuery": "QueryBuildkiteAnnotations",
    "BuildkiteFlakyTestQuery": "QueryBuildkiteFlakyTest",
    "BuildkiteArtifactQuery": "QueryBuildkiteArtifacts",

    // Datadog
    "DatadogCIPipelineQuery": "QueryDatadogCIPipelineEvents",
    "DatadogCIPipelineAggregateQuery": "QueryDatadogCIPipelineEventsAggregation",
    "DatadogDashboardQuery": "QueryDatadogDashboard",
    "DatadogNotebookQuery": "QueryDatadogNotebook",
    "DatadogEventQuery": "QueryDatadogEvents",
    "DatadogLogQuery": "QueryDatadogLogs",
    "DatadogLogAggregationQuery": "QueryDatadogLogsAggregation",
    "DatadogMonitorQuery": "QueryDatadogMonitor",
    "DatadogRUMAnalyticsAggregateQuery": "QueryDatadogRUMAggregation",
    "DatadogServiceDependencyQuery": "QueryDatadogServiceDependency",
    "DatadogErrorTrackingIssueDetailsQuery": "QueryDatadogErrorTrackingIssueDetails",
    "DatadogErrorTrackingIssuesQuery": "QueryDatadogErrorTrackingIssues",
    "DatadogSpanQuery": "QueryDatadogSpans",
    "DatadogSpanAggregationQuery": "QueryDatadogSpansAggregation",
    "DatadogTimeseriesMetricQuery": "QueryDatadogMetrics",
    "DatadogMetricSearchQuery": "SearchDatadogMetrics",
    "DatadogMetricTagsQuery": "QueryDatadogMetricTags",
    "DatadogSLOQuery": "QueryDatadogSLOs",
    "DatadogSLOPerformanceQuery": "QueryDatadogSLOsPerformance",
    "DatadogSLODailyPerformanceQuery": "QueryDatadogSLODailyPerformance",
    "DatadogSyntheticTestSearchQuery": "SearchDatadogSyntheticTests",
    "DatadogSyntheticTestQuery": "QueryDatadogSyntheticTest",
    "DatadogSyntheticTestResultsQuery": "QueryDatadogSyntheticTestResults",
    "DatadogSyntheticTestResultQuery": "QueryDatadogSyntheticTestResult",
    "DatadogSyntheticCIBatchQuery": "QueryDatadogSyntheticCIBatch",

    // CloudWatch
    "CloudWatchLogQuery": "QueryCloudWatchLogs",
    "CloudWatchMetricQuery": "QueryCloudWatchMetrics",

    // GCP Cloud Monitoring
    "GCPCloudMonitoringLogQuery": "QueryGCPCloudMonitoringLogs",
    "GCPCloudMonitoringMetricQuery": "QueryGCPCloudMonitoringMetrics",
    "GCPCloudMonitoringLogBucketsQuery": "QueryGCPCloudMonitoringLogBuckets",
    "GCPCloudMonitoringLogsAggregationQuery": "QueryGCPCloudMonitoringLogsAggregation",

    // Grafana Cloud
    "GrafanaCloudLogQuery": "QueryGrafanaLogs",
    "GrafanaCloudLogAggregationQuery": "QueryGrafanaLogsAggregation",
    "GrafanaCloudSpanQuery": "QueryGrafanaSpans",
    "GrafanaCloudSpanAggregationQuery": "QueryGrafanaSpansAggregation",
    "GrafanaCloudTimeseriesMetricQuery": "QueryGrafanaMetrics",

    // New Relic
    "NewRelicLogQuery": "QueryNewRelicLogs",
    "NewRelicLogAggregationQuery": "QueryNewRelicLogsAggregation",
    "NewRelicSpanQuery": "QueryNewRelicSpans",
    "NewRelicSpanAggregationQuery": "QueryNewRelicSpansAggregation",
    "NewRelicTransactionQuery": "QueryNewRelicTransactions",
    "NewRelicTransactionAggregationQuery": "QueryNewRelicTransactionsAggregation",
    "NewRelicTimeseriesMetricQuery": "QueryNewRelicMetrics",
    "NewRelicDashboardQuery": "QueryNewRelicDashboard",
    "NewRelicChangeTrackingQuery": "QueryNewRelicChangeTracking",

    // GitHub
    "GitHubCommitsQuery": "QueryGitHubCommits",
    "GitHubActionsRunQuery": "QueryGitHubActionsRun",
    "GitHubActionsLogsQuery": "QueryGitHubActionsLogs",
    "CodeQuery": "QueryCode",

    // Slack
    "SlackChannelContentsQuery": "QuerySlackChannelContents",
    "SlackChannelMetadataQuery": "QuerySlackChannelMetadata",

    // Sentry
    "SentryIssuesQuery": "QuerySentryIssues",
    "SentryIssueEventsQuery": "QuerySentryIssueEvents",
    "SentryTraceQuery": "QuerySentryTraceDetails",
    "SentryTracesQuery": "QuerySentryTraces",

    // Temporal
    "TemporalWorkflowsQuery": "QueryTemporalWorkflows",
    "TemporalWorkflowHistoryQuery": "QueryTemporalWorkflowHistory",

    // Rootly
    "RootlyDashboardQuery": "QueryRootlyDashboard",
    "RootlyIncidentQuery": "QueryRootlyIncidents",
    "RootlyIncidentDetailQuery": "QueryRootlyIncident",
    "RootlyAlertQuery": "QueryRootlyAlerts",
    "RootlyAlertDetailQuery": "QueryRootlyAlert",
    "RootlyIncidentAlertsQuery": "QueryRootlyIncidentAlerts",

    // Confluence
    "ConfluenceDocumentsQuery": "QueryConfluenceDocuments",
    "SearchConfluenceDocumentsQuery": "SearchConfluenceDocuments",

    // Notion
    "NotionDocumentsQuery": "QueryNotionDocuments",
    "SearchNotionDocumentsQuery": "SearchNotionDocuments",

    // Web
    "SearchWebQuery": "SearchWeb",
    "FetchWebContentQuery": "FetchWebContent",

    // LangSmith
    "LangSmithRunsQuery": "QueryLangSmithRuns",
    "LangSmithRunStatsQuery": "GetLangSmithRunStats",

    // Capabilities
    "RunCapabilityQuery": "RunCapability",

    // PGAnalyze
    "PGAnalyzeServerInfoQuery": "QueryPGAnalyzeServerInfo",
    "PGAnalyzeDatabaseInfoQuery": "QueryPGAnalyzeDatabaseInfo",
    "PGAnalyzeSchemaQuery": "QueryPGAnalyzeSchema",
    "PGAnalyzeQueriesQuery": "QueryPGAnalyzeQueries",
    "PGAnalyzeIssuesQuery": "QueryPGAnalyzeIssues",
    "PGAnalyzeVacuumsQuery": "QueryPGAnalyzeVacuums",
    "PGAnalyzeLogsQuery": "QueryPGAnalyzeLogs",
    "PGAnalyzeConnectionsQuery": "QueryPGAnalyzeConnections",

    // Datadog Incident
    "DatadogIncidentQuery": "QueryDatadogIncident",

    // PagerDuty
    "PagerDutyIncidentListQuery": "QueryPagerDutyIncidents",
    "PagerDutyIncidentDetailQuery": "QueryPagerDutyIncident",
    "PagerDutyIncidentAlertsQuery": "QueryPagerDutyIncidentAlerts",
    "PagerDutyIncidentNotesQuery": "QueryPagerDutyIncidentNotes",

    // FireHydrant
    "FireHydrantIncidentListQuery": "QueryFireHydrantIncidents",
    "FireHydrantIncidentDetailQuery": "QueryFireHydrantIncident",
    "FireHydrantIncidentAlertsQuery": "QueryFireHydrantIncidentAlerts",
    "FireHydrantIncidentExternalResourcesQuery": "QueryFireHydrantExternalResources",
    "FireHydrantIncidentBridgeSummariesQuery": "QueryFireHydrantBridgeSummaries",
    "FireHydrantAlertListQuery": "QueryFireHydrantAlerts",
    "FireHydrantAlertDetailQuery": "QueryFireHydrantAlert",
    "FireHydrantRetrospectivesQuery": "QueryFireHydrantRetrospectives",
    "FireHydrantPostMortemReportsQuery": "QueryFireHydrantPostMortems",

    // Incident.io
    "IncidentIoIncidentListQuery": "QueryIncidentIoIncidents",
    "IncidentIoIncidentDetailQuery": "QueryIncidentIoIncident",
    "IncidentIoIncidentAlertsQuery": "QueryIncidentIoIncidentAlerts",
    "IncidentIoAlertDetailQuery": "QueryIncidentIoAlert",

    // Elasticsearch
    "ElasticsearchLogQuery": "QueryElasticsearchLogs",
    "ElasticsearchAggregationQuery": "QueryElasticsearchAggregation",
    "ElasticsearchIndicesQuery": "QueryElasticsearchIndices",

    // Prometheus
    "PrometheusMetricsQuery": "QueryPrometheusMetrics",
    "PrometheusLabelValuesQuery": "QueryPrometheusLabelValues",
    "PrometheusTargetsQuery": "QueryPrometheusTargets",

    // GCP Dashboard
    "GCPCloudMonitoringDashboardQuery": "QueryGCPCloudMonitoringDashboard",

    // Jira
    "JiraIssueQuery": "QueryJiraIssue",
    "JiraSearchQuery": "SearchJiraIssues",

    // Linear
    "LinearSearchIssuesQuery": "SearchLinearIssues",
    "LinearIssueQuery": "QueryLinearIssue",
    "LinearCreateIssueQuery": "CreateLinearIssue",
    "LinearUpdateIssueQuery": "UpdateLinearIssue",
    "LinearCommentQuery": "AddLinearComment",
    "LinearTeamsQuery": "QueryLinearTeams",

    // PostgreSQL
    "RunPostgresQueryQuery": "RunPostgresQuery",

    // Custom MCP
    "ListCustomMcpServerToolsQuery": "ListCustomMcpTools",
    "CallCustomMcpServerToolQuery": "CallCustomMcpTool",

    // CLI Sandbox
    "RunCliCmdQuery": "RunCliCmd",
  };

  return queryTypeMap[queryType] || queryType;
}

const serializeSourceQuery = (query: Source["query"]): string => {
  if (!query) {
    return "";
  }

  return typeof query === "string" ? query : JSON.stringify(query, null, 2);
};

function formatQueryForTooltip(query: string | null | undefined): string | null {
  if (!query) return null;

  try {
    const isJson = query.trim().startsWith("{");
    if (!isJson) return query;

    const queryObject = JSON.parse(query);
    // Return formatted JSON
    return JSON.stringify(queryObject, null, 2);
  } catch {
    return query;
  }
}

function QueryTooltip({
  children,
  fullQuery,
}: {
  children: string;
  fullQuery: string | null | undefined;
}) {
  const formattedQuery = formatQueryForTooltip(fullQuery);

  if (!formattedQuery) {
    return <span>{children}</span>;
  }

  return (
    <Tooltip
      content={<div className="font-mono whitespace-pre-wrap break-all">{formattedQuery}</div>}
      maxWidth="32rem"
      position="top"
      interactive
    >
      <span className="cursor-help">{children}</span>
    </Tooltip>
  );
}

function getSourceDisplayText(source: Source): string {
  if (source.displayText) {
    return source.displayText;
  }

  if (!source.query) {
    return "Source";
  }

  try {
    const isJson = typeof source.query === "string" && source.query.trim().startsWith("{");
    const queryObject = isJson ? JSON.parse(source.query) : null;

    if (queryObject?.filter?.query) {
      const text = queryObject.filter.query as string;
      return `${text.substring(0, 120)}${text.length > 120 ? "..." : ""}`;
    }
    const raw = typeof source.query === "string" ? source.query : JSON.stringify(source.query);
    return `${raw.substring(0, 120)}${raw.length > 120 ? "..." : ""}`;
  } catch {
    const raw = typeof source.query === "string" ? source.query : JSON.stringify(source.query);
    return `${raw.substring(0, 120)}${raw.length > 120 ? "..." : ""}`;
  }
}

interface SourceItemProps {
  source: Source;
  index: number;
  isActive: boolean;
  sourceRef?: React.RefCallback<HTMLDivElement> | null;
  onSourceClick?: (sourceId: string) => void;
}

const SourceItem: React.FC<SourceItemProps> = ({ source, index, isActive, sourceRef, onSourceClick }) => {
  if (!source.toolId) {
    return (
      <div className="flex flex-col w-full items-start p-2 rounded text-xs text-gray-400">
        <div className="flex items-start">
          <span className="mr-1 text-primary font-semibold">[{index}]</span>
          <span className="w-3.5 h-3.5 mr-2" />
          <span>Source without ID</span>
        </div>
      </div>
    );
  }

  const displayText = getSourceDisplayText(source);
  const tooltipQuery = source.query || null;
  const sourceId = source.sourceId ?? null;
  const isInteractive = Boolean(onSourceClick && sourceId);

  const handleClick = isInteractive && onSourceClick && sourceId ? () => onSourceClick(sourceId) : undefined;
  const handleKeyDown =
    isInteractive && onSourceClick && sourceId
      ? (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSourceClick(sourceId);
        }
      }
      : undefined;

  return (
    <div
      ref={sourceRef || null}
      className={cn(
        "flex flex-col w-full items-start p-2 rounded text-xs",
        isActive ? "bg-primary/20 text-white" : "text-muted-foreground",
      )}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="flex flex-col w-full">
        {source.link ? (
          <div className="flex flex-col lg:flex-row sm:items-start lg:justify-between w-full gap-y-1">
            <div className="flex items-start">
              <span className="mr-1 text-primary font-semibold">[{index}]</span>
              <span className="break-all mr-1">
                <QueryTooltip fullQuery={tooltipQuery}>{displayText}</QueryTooltip>
              </span>
            </div>
            <div className="flex-shrink-0">
              <SourceLink
                link={source.link}
                linkText={getQueryDisplayName(source.type)}
                tooltip={source.type === "GCPCloudMonitoringLogsAggregationQuery" ? "Copy and paste the query into BigQuery to see the evidenced data" : undefined}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-start">
            <span className="mr-1 text-primary font-semibold">[{index}]</span>
            <QueryTooltip fullQuery={tooltipQuery}>{displayText}</QueryTooltip>
          </div>
        )}

        {source.artifactId != null && source.artifactId.length > 0 && (
          <div className="mt-2 w-full">
            <ErrorBoundary
              fallback={
                <div className="p-2 text-xs bg-red-950/30 border border-red-800/50 rounded text-red-400">
                  Unable to load artifact data. Try refreshing the page.
                </div>
              }
            >
              <ArtifactCard
                artifactId={source.artifactId}
                groupTags={source.groupTags ?? []}
                title={source.title || source.displayText || displayText || "Artifact"}
                description={source.description || ""}
                query={serializeSourceQuery(source.query)}
                sourceLink={source.link || undefined}
                sourceName={getQueryDisplayName(source.type)}
                prefetchedArtifact={source.prefetchedArtifact as React.ComponentProps<typeof ArtifactCard>["prefetchedArtifact"]}
              />
            </ErrorBoundary>
          </div>
        )}
      </div>
    </div>
  );
};

interface SourcesSectionProps {
  sources: Source[];
  activeSourceId: string | null;
  onSourceClick?: (sourceId: string) => void;
  usage?: { readonly billingTokens: number } | null;
  copyButton?: React.ReactNode;
  forceInlineArtifacts?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const SourcesSection: React.FC<SourcesSectionProps> = ({
  sources,
  activeSourceId,
  onSourceClick,
  usage,
  copyButton,
  forceInlineArtifacts = false,
  isExpanded: controlledExpanded,
  onExpandedChange,
}) => {
  const isControlled = typeof controlledExpanded === "boolean";
  const [internalExpanded, setInternalExpanded] = React.useState<boolean>(true);
  const isExpanded = isControlled ? controlledExpanded! : internalExpanded;

  React.useEffect(() => {
    if (!isControlled && forceInlineArtifacts) {
      setInternalExpanded(true);
    }
  }, [forceInlineArtifacts, isControlled]);

  const setExpanded = React.useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalExpanded(next);
      }
      onExpandedChange?.(next);
    },
    [isControlled, onExpandedChange],
  );

  const sourceRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  React.useEffect(() => {
    if (activeSourceId) {
      setExpanded(true);
      setTimeout(() => {
        const selectedElement = document.getElementById(`source-${activeSourceId}`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 100);
    }
  }, [activeSourceId, setExpanded]);

  if (sources.length === 0) {
    return null;
  }

  const sourceIdToIndex: Record<string, number> = {};
  sources.forEach((source, i) => {
    if (source.sourceId) {
      sourceIdToIndex[source.sourceId] = i + 1;
    }
  });

  return (
    <div className="mt-4 pt-3 border-t border-border">
      <div
        className="text-primary text-xs uppercase font-semibold tracking-wide mb-2 flex items-center cursor-pointer"
        onClick={() => setExpanded(!isExpanded)}
      >
        <svg
          className={`w-4 h-4 text-primary transform transition-transform mr-1 ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <span>Sources ({sources.length})</span>
      </div>

      {isExpanded && (
        <div className="space-y-2 overflow-y-auto custom-scrollbar pr-2">
          {sources.map((source, i) => (
            <div key={source.sourceId || `source-${i}`} id={`source-${source.sourceId}`}>
              <SourceItem
                source={source}
                index={sourceIdToIndex[source.sourceId] || i + 1}
                isActive={activeSourceId === source.sourceId}
                sourceRef={source.sourceId ? (el) => (sourceRefs.current[source.sourceId] = el) : null}
                onSourceClick={onSourceClick}
              />
            </div>
          ))}
        </div>
      )}

      {(usage || copyButton) && (
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          {usage ? <span>Tokens used: {usage.billingTokens}</span> : <span />}
          {copyButton ?? null}
        </div>
      )}
    </div>
  );
};
